/**
 * Os sete segmentos do CRM como o dono vê: nome, cor de reconhecimento, ícone,
 * quem está ali e o que fazer. Mesma ordem nas duas telas (ação primeiro).
 * Regras em Servidor/docs/requisito-crm-segmentos-v1.md.
 */
export type CodigoSegmento = 'ESFRIANDO' | 'SUMIDO' | 'NOVO' | 'FIEL' | 'CAMPEAO' | 'PERDIDO' | 'SEM_PEDIDO';

export interface SegmentoCrm {
  codigo: CodigoSegmento;
  nome: string;
  nomeSingular: string;
  cor: string;
  fundo: string;
  icone: string;
  quem: string;
  acao: string;
  /** ordenação que a consulta de contatos entende (ordemCrm) */
  ordem: 'ATRASO' | 'VALOR' | 'RECENTE' | 'NOME';
  /** segmentos em que o atraso sobre o ritmo de compra é a informação principal */
  mostraAtraso: boolean;
}

export const SEGMENTOS_CRM: SegmentoCrm[] = [
  {codigo: 'ESFRIANDO', nome: 'Esfriando', nomeSingular: 'Esfriando', cor: '#d97706', fundo: '#fff6e5', icone: 'fe-thermometer',
    quem: 'Passaram do próprio ritmo de compra e ainda não voltaram.',
    acao: 'Chame agora, com mensagem pessoal e um motivo para pedir hoje. É a janela com mais retorno.',
    ordem: 'ATRASO', mostraAtraso: true},
  {codigo: 'SUMIDO', nome: 'Sumidos', nomeSingular: 'Sumido', cor: '#ea580c', fundo: '#fff0e8', icone: 'fe-clock',
    quem: 'Estão há mais tempo sem pedir do que o corte de Sumido.',
    acao: 'Reative com um cupom de prazo curto e diga que sentiu falta.',
    ordem: 'ATRASO', mostraAtraso: true},
  {codigo: 'NOVO', nome: 'Novos', nomeSingular: 'Novo', cor: '#2563eb', fundo: '#ebf2ff', icone: 'fe-star',
    quem: 'Fizeram até 2 pedidos e ainda estão em dia.',
    acao: 'Agradeça e puxe o 2º pedido na primeira semana. Cliente que pede 3 vezes costuma ficar.',
    ordem: 'RECENTE', mostraAtraso: false},
  {codigo: 'FIEL', nome: 'Fiéis', nomeSingular: 'Fiel', cor: '#16a34a', fundo: '#e9f8ef', icone: 'fe-heart',
    quem: 'Compram no ritmo de sempre.',
    acao: 'Suba o ticket: combo, adicional ou a novidade da semana.',
    ordem: 'VALOR', mostraAtraso: false},
  {codigo: 'CAMPEAO', nome: 'Campeões', nomeSingular: 'Campeão', cor: '#7c5cff', fundo: '#f2eeff', icone: 'fe-award',
    quem: 'Pedem muito e gastam bem, em dia com o ritmo.',
    acao: 'Reconheça sem desconto: novidade em primeira mão, mimo surpresa, atendimento pelo nome.',
    ordem: 'VALOR', mostraAtraso: false},
  {codigo: 'PERDIDO', nome: 'Perdidos', nomeSingular: 'Perdido', cor: '#64748b', fundo: '#f0f3f7', icone: 'fe-user-x',
    quem: 'Passaram do corte de Perdido sem pedir.',
    acao: 'Só vale campanha esporádica com oferta forte. Não gaste atendimento um a um aqui.',
    ordem: 'NOME', mostraAtraso: false},
  {codigo: 'SEM_PEDIDO', nome: 'Sem pedido', nomeSingular: 'Sem pedido', cor: '#0d9488', fundo: '#e7f7f4', icone: 'fe-user-plus',
    quem: 'Cadastrados que ainda não fizeram o primeiro pedido.',
    acao: 'Convide para o primeiro pedido com o link do cardápio.',
    ordem: 'NOME', mostraAtraso: false},
];

export function segmentoCrm(codigo: string): SegmentoCrm {
  return SEGMENTOS_CRM.find(s => s.codigo === codigo) || SEGMENTOS_CRM[0];
}

const DIA_MS = 24 * 60 * 60 * 1000;

function inicioDoDia(data: Date): number {
  return new Date(data.getFullYear(), data.getMonth(), data.getDate()).getTime();
}

/** Dias de calendário desde o último pedido válido (calculado agora, não o cache da última avaliação). */
export function diasSemPedir(contato: any): number | null {
  if (!contato || !contato.crmUltimoPedidoEm) return contato && contato.diasSemPedir != null ? Number(contato.diasSemPedir) : null;
  return Math.max(0, Math.round((inicioDoDia(new Date()) - inicioDoDia(new Date(contato.crmUltimoPedidoEm))) / DIA_MS));
}

export function textoDias(dias: number | null): string {
  if (dias === null) return 'nunca pediu';
  if (dias === 0) return 'pediu hoje';
  if (dias === 1) return 'pediu ontem';
  return `há ${dias} dias`;
}

/** Quanto passou do ritmo de compra (0 quando em dia). */
export function diasDeAtraso(contato: any): number {
  const dias = diasSemPedir(contato);
  const ciclo = Number(contato && contato.cicloRecompraDias) || 0;
  if (dias === null || !ciclo) return 0;
  return Math.max(0, dias - ciclo);
}

export function iniciais(nome: string): string {
  const partes = String(nome || '').trim().split(/\s+/).filter(Boolean);
  if (!partes.length) return '?';
  const primeira = partes[0][0] || '';
  const ultima = partes.length > 1 ? partes[partes.length - 1][0] : '';
  return (primeira + ultima).toUpperCase();
}

export function telefoneFormatado(telefone: string): string {
  const d = String(telefone || '').replace(/\D/g, '');
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return telefone || '';
}

export function linkWhatsApp(contato: any): string {
  const pais = String((contato && contato.codigoPais) || '+55').replace(/\D/g, '');
  const numero = String((contato && contato.telefone) || '').replace(/\D/g, '');
  return `https://wa.me/${pais}${numero}`;
}

export function recebeMensagem(contato: any): boolean {
  return !!contato && !contato.bloqueado && !contato.desativarMensagensDeMarketing;
}

export function moeda(valor: number): string {
  return 'R$ ' + (Number(valor) || 0).toLocaleString('pt-BR', {minimumFractionDigits: 2, maximumFractionDigits: 2});
}
